import React, { useState, useCallback } from 'react'
import { Stack, Box, TextArea, Button, Flex, Text } from '@sanity/ui'
import { EditIcon } from '@sanity/icons'
import { StringInputProps, set, unset } from 'sanity'
import { TemplateEditorModal } from './TemplateEditorModal'

// Use the existing StringInputProps type without extending schemaType
type PromptTemplateInputProps = StringInputProps

/**
 * PromptTemplateInput - Custom input for prompt templates with modal editor
 *
 * Features:
 * - Standard textarea for quick edits
 * - "Open Editor" button to launch full modal editor with:
 *   - Field picker for inserting context variables
 *   - Live preview with real buyer guide data
 */
export function PromptTemplateInput(props: PromptTemplateInputProps) {
  const { value = '', onChange, schemaType, elementProps } = props
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Get maxLength from schema options if available
  const maxLength = (schemaType.options as { maxLength?: number } | undefined)?.maxLength

  // Handle textarea change
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value
      onChange(newValue ? set(newValue) : unset())
    },
    [onChange]
  )

  // Handle modal save
  const handleModalSave = useCallback(
    (newValue: string) => {
      onChange(newValue ? set(newValue) : unset())
    },
    [onChange]
  )

  // Get a user-friendly title for the modal
  const getModalTitle = () => {
    const fieldName = schemaType.title || schemaType.name || 'Template'
    return `Edit ${fieldName}`
  }

  return (
    <>
      <Stack gap={3}>
        {/* Textarea with row count */}
        <Box>
          <TextArea
            {...elementProps}
            value={value}
            onChange={handleChange}
            rows={8}
            style={{
              fontFamily: 'monospace',
              fontSize: '13px',
              lineHeight: 1.5,
            }}
            placeholder="Enter prompt template. Use {{variable}} for context values."
          />
        </Box>

        {/* Footer with char count and Open Editor button */}
        <Flex justify="space-between" align="center">
          <Box>
            {maxLength && (
              <Text size={0} muted>
                {(value || '').length} / {maxLength} characters
              </Text>
            )}
          </Box>
          <Button
            text="Open Editor"
            icon={EditIcon}
            mode="ghost"
            tone="primary"
            onClick={() => setIsModalOpen(true)}
          />
        </Flex>

        {/* Help text */}
        <Text size={0} muted>
          Use {'{{variable}}'} for context values. Click "Open Editor" for field picker and live preview.
        </Text>
      </Stack>

      {/* Modal Editor */}
      <TemplateEditorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={getModalTitle()}
        value={value || ''}
        onChange={handleModalSave}
        maxLength={maxLength}
      />
    </>
  )
}

export default PromptTemplateInput
